import type { TextInputProps } from '@mantine/core';
import { Input, useComputedColorScheme } from '@mantine/core';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { forwardRef, useMemo, useRef, useState } from 'react';
import type { ExecutorVar } from '~/common/vm/protocol';
import { addAllLibTypes, addVarsType, setupTypescript } from '~/logic/monaco/setup-typescript';
import { setupMonacoThemes } from '~/logic/monaco/setup-theme';

const Editor = dynamic(() => import('@monaco-editor/react'), { ssr: false });

// Helper function to check if a string is strictly valid JSON and format it
const formatIfJson = (value: string): string => {
  try {
    const parsedJson = JSON.parse(value);
    return JSON.stringify(parsedJson, null, 2);
  } catch (e) {
    // Not valid JSON, return original value
    return value;
  }
};

// Constants for line height calculation
const LINE_HEIGHT = 20; // Approximate line height in pixels
const EDITOR_PADDING = 12; // Top and bottom padding combined

export const MonacoInputField = forwardRef<
  HTMLDivElement,
  TextInputProps & {
    readonlyMessage?: string;
    variables?: ExecutorVar[];
    className?: string;
    minLines?: number;
    maxLines?: number;
  }
>(
  (
    {
      className,
      label,
      labelProps,
      inputWrapperOrder,
      autoFocus,
      value,
      disabled,
      defaultValue,
      error,
      onChange,
      onFocus,
      onBlur,
      readonlyMessage,
      variables,
      leftSection,
      leftSectionWidth,
      rightSection,
      minLines = 4,
      maxLines = 8,
    },
    ref,
  ) => {
    const colorScheme = useComputedColorScheme();
    const [focused, setFocused] = useState(autoFocus);
    const [editorHeight, setEditorHeight] = useState(minLines * LINE_HEIGHT + EDITOR_PADDING);
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const editorRef = useRef<any>(null); // Can't seem to find the type: editor.IStandaloneCodeEditor. This is used for updating editor height

    // Format value if it's valid JSON, and only format on first render
    const formattedValue = useMemo(() => (value !== undefined ? formatIfJson(`${value}`) : undefined), []);

    const updateEditorHeight = () => {
      if (!editorRef.current) {
        return;
      }

      const model = editorRef.current.getModel();
      if (!model) {
        return;
      }

      const lineCount = model.getLineCount();
      const visibleLines = Math.min(Math.max(lineCount, minLines), maxLines);
      setEditorHeight(visibleLines * LINE_HEIGHT + EDITOR_PADDING);
    };

    return (
      <Input.Wrapper
        label={label}
        labelProps={labelProps}
        error={error}
        inputWrapperOrder={inputWrapperOrder}
        className={className}
      >
        <div
          ref={ref}
          className={clsx('flex flex-row border-y-[1px]', focused ? `border-[#1b64a7]` : `border-[#424242]`, {
            'border-l-[1px]': !leftSection,
            'border-r-[1px]': !rightSection,
          })}
        >
          {!!leftSection && (
            <div
              style={{ width: leftSectionWidth }}
              className={clsx(
                'flex items-center justify-center whitespace-nowrap rounded-sm border-l-[1px] dark:bg-[#2e2e2e]',
                focused ? `border-l-[#1b64a7]` : `border-l-[#424242]`,
              )}
            >
              <span className="pl-2 text-[#828282]">{leftSection}</span>
            </div>
          )}
          <Editor
            height={`${editorHeight}px`}
            defaultLanguage="typescript"
            defaultValue={defaultValue !== undefined ? `${defaultValue}` : undefined}
            value={formattedValue}
            theme={colorScheme === 'dark' ? 'mantine-dark' : 'light'}
            beforeMount={async (monaco) => {
              setupMonacoThemes(monaco);
              setupTypescript(monaco);
              await addAllLibTypes(monaco, { playwrightTypeSelector: { pageLocators: true } });
              if (variables) {
                addVarsType(monaco, variables);
              }
            }}
            onMount={(editor, _monaco) => {
              editorRef.current = editor;
              if (autoFocus) {
                editor.focus();
                editor.setPosition({ lineNumber: 1, column: 1 });
              }

              // Initial height adjustment
              updateEditorHeight();

              editor.onDidFocusEditorText(() => {
                const editorValue = editor.getValue();
                onFocus?.({
                  currentTarget: { value: editorValue },
                  target: { value: editorValue },
                } as React.FocusEvent<HTMLInputElement>);
                setFocused(true);
              });
              editor.onDidBlurEditorText(() => {
                const editorValue = editor.getValue();
                onBlur?.({
                  currentTarget: { value: editorValue },
                  target: { value: editorValue },
                } as React.FocusEvent<HTMLInputElement>);
                setFocused(false);
              });

              // Update height when content changes
              editor.onDidChangeModelContent(() => {
                updateEditorHeight();
              });
            }}
            onChange={(value) => {
              // This is hacky, but it works (not sure why)
              onChange?.(value as unknown as React.ChangeEvent<HTMLInputElement>);
            }}
            options={{
              tabFocusMode: false,
              readOnly: disabled,
              readOnlyMessage: readonlyMessage ? { value: readonlyMessage } : undefined,
              scrollbar: {
                vertical: 'hidden',
                horizontal: 'hidden',
                useShadows: false,
              },
              padding: {
                top: 6,
                bottom: 6,
              },
              overviewRulerLanes: 0,
              lineNumbers: 'off',
              minimap: { enabled: false },
              fontSize: 14,
              scrollBeyondLastLine: false,

              lineNumbersMinChars: 0,
              glyphMargin: false,
              folding: false,
              renderLineHighlight: 'none',
              contextmenu: false,
              snippetSuggestions: 'none',
              linkedEditing: false,

              formatOnType: false,
              formatOnPaste: false,
              selectionHighlight: false,
            }}
            className="mantine-Input-input"
          />
          {!!rightSection && (
            <div
              className={clsx(
                'flex items-center justify-center whitespace-nowrap rounded-sm border-r-[1px] dark:bg-[#2e2e2e]',
                focused ? `border-r-[#1b64a7]` : `border-r-[#424242]`,
              )}
            >
              <span className="pr-2 text-[#828282]">{rightSection}</span>
            </div>
          )}
        </div>
      </Input.Wrapper>
    );
  },
);

MonacoInputField.displayName = 'MonacoInput';
